<template>
  <section class="page-container home-board-page">
    <div class="toolbar">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="Place">
          <el-select v-model="filters.placeType" @change="changePlaceType">
            <el-option label="Home" value="HOME" />
            <el-option label="Mall Category" value="MALL_CAT" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="filters.placeType === 'MALL_CAT'" label="Category">
          <el-select
            v-model="filters.placeId"
            clearable
            filterable
            placeholder="Root category"
            @change="searchBoards"
          >
            <el-option
              v-for="item in rootCats"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Status">
          <status-select v-model="filters.status" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchBoards">Search</el-button>
        </el-form-item>
        <el-form-item>
          <el-button type="success" @click="showBoardCreate">New Board</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-row :gutter="16">
      <el-col :lg="14" :md="24">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-header">
            <span>{{ boardListTitle }}</span>
            <el-button type="text" size="mini" @click="listBoards">Refresh</el-button>
          </div>
          <el-table
            ref="boardTable"
            v-loading="boardLoading"
            :data="boards"
            border
            highlight-current-row
            size="mini"
            @current-change="handleBoardSelect"
          >
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column label="Page" width="160">
              <template slot-scope="scope">
                {{ placeLabel(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column prop="title" label="Title" min-width="150" />
            <el-table-column prop="type" label="Type" width="130">
              <template slot-scope="scope">
                <el-tag size="mini">{{ scope.row.type }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sortNo" label="Sort" width="80" />
            <el-table-column prop="status" label="Status" width="100">
              <template slot-scope="scope">{{ scope.row.status | status }}</template>
            </el-table-column>
            <el-table-column prop="updateTime" label="Updated" width="160">
              <template slot-scope="scope">{{ scope.row.updateTime | formatDate }}</template>
            </el-table-column>
            <el-table-column label="Action" width="150" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click.stop="showBoardEdit(scope.row)">Edit</el-button>
                <el-button size="mini" type="danger" @click.stop="deleteBoard(scope.row)"
                  >Delete</el-button
                >
              </template>
            </el-table-column>
          </el-table>
          <my-pagination
            :page="boardPage"
            @current-change="handleBoardPageChange"
            @size-change="handleBoardLimitChange"
          />
        </el-card>
      </el-col>

      <el-col :lg="10" :md="24">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-header">
            <span>{{ selectedBoard ? selectedBoard.title : "Board Contents" }}</span>
            <el-button
              type="primary"
              size="mini"
              :disabled="!canManageContents"
              @click="showContentCreate"
            >
              New Content
            </el-button>
          </div>

          <el-empty v-if="!selectedBoard" description="Select a board first" />
          <div v-else>
            <el-alert
              v-if="!canManageContents"
              :closable="false"
              type="info"
              show-icon
              title="This board type is generated by app data and has no manual content items."
              class="content-alert"
            />
            <el-table
              v-loading="contentLoading"
              :data="contents"
              border
              size="mini"
              empty-text="No contents"
            >
              <el-table-column prop="id" label="ID" width="70" />
              <el-table-column label="Content" min-width="220">
                <template slot-scope="scope">
                  <div v-if="selectedBoard.type === 'BANNER'" class="banner-cell">
                    <el-image
                      v-if="getBanner(scope.row).imageUrl"
                      class="banner-thumb"
                      :src="getBanner(scope.row).imageUrl"
                      :preview-src-list="imagePreviewList(getBanner(scope.row).imageUrl)"
                      fit="cover"
                    />
                    <div class="content-text">
                      <div class="content-title">{{ getBanner(scope.row).title || "-" }}</div>
                      <div class="content-link">{{ getBanner(scope.row).actionUrl || "-" }}</div>
                    </div>
                  </div>
                  <div v-else-if="selectedBoard.type === 'PRODUCT'">
                    <div>Product ID: {{ getProductId(scope.row) || "-" }}</div>
                    <div class="content-title">
                      {{ scope.row.product && scope.row.product.name }}
                    </div>
                  </div>
                  <div v-else-if="selectedBoard.type === 'BRAND'">
                    <div>Category ID: {{ getBrandId(scope.row) || "-" }}</div>
                    <div class="content-title">{{ scope.row.brand && scope.row.brand.name }}</div>
                  </div>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="sortNo" label="Sort" width="70" />
              <el-table-column label="Action" width="140">
                <template slot-scope="scope">
                  <el-button
                    size="mini"
                    :disabled="!canManageContents"
                    @click="showContentEdit(scope.row)"
                  >
                    Edit
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    :disabled="!canManageContents"
                    @click="deleteContent(scope.row)"
                  >
                    Delete
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <my-pagination
              :page="contentPage"
              @current-change="handleContentPageChange"
              @size-change="handleContentLimitChange"
            />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog
      :title="boardDialogMode === 'create' ? 'New Board' : 'Edit Board'"
      :visible.sync="boardDialogVisible"
      :close-on-click-modal="false"
      width="560px"
    >
      <el-form :model="boardForm" label-width="110px">
        <el-form-item label="Place">
          <el-select
            v-model="boardForm.placeType"
            :disabled="boardDialogMode === 'edit'"
            @change="changeBoardFormPlace"
          >
            <el-option label="Home" value="HOME" />
            <el-option label="Mall Category" value="MALL_CAT" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="boardForm.placeType === 'MALL_CAT'" label="Category">
          <el-select
            v-model="boardForm.placeId"
            :disabled="boardDialogMode === 'edit'"
            filterable
            placeholder="Root category"
          >
            <el-option
              v-for="item in rootCats"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Title">
          <el-input v-model="boardForm.title" maxlength="32" />
        </el-form-item>
        <el-form-item label="Type">
          <el-select v-model="boardForm.type" :disabled="boardDialogMode === 'edit'">
            <el-option
              v-for="item in boardTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Sort">
          <el-input-number v-model="boardForm.sortNo" :min="0" />
        </el-form-item>
        <el-form-item label="Status">
          <status-select v-model="boardForm.status" />
        </el-form-item>
        <el-form-item label="Remark">
          <el-input v-model="boardForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="boardDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="boardSaving" @click="submitBoard">Save</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="contentDialogMode === 'create' ? 'New Content' : 'Edit Content'"
      :visible.sync="contentDialogVisible"
      :close-on-click-modal="false"
      width="620px"
    >
      <el-form :model="contentForm" label-width="120px">
        <el-form-item label="Board">
          <el-input :value="selectedBoard ? selectedBoard.title : ''" disabled />
        </el-form-item>
        <el-form-item label="Sort">
          <el-input-number v-model="contentForm.sortNo" :min="0" />
        </el-form-item>

        <template v-if="selectedBoard && selectedBoard.type === 'BANNER'">
          <el-form-item label="Image">
            <image-upload v-model="contentForm.banner.imageUrl" />
          </el-form-item>
          <el-form-item label="Title">
            <el-input v-model="contentForm.banner.title" />
          </el-form-item>
          <el-form-item label="Action URL">
            <el-input v-model="contentForm.banner.actionUrl" />
          </el-form-item>
        </template>

        <template v-if="selectedBoard && selectedBoard.type === 'PRODUCT'">
          <el-form-item label="Product ID">
            <el-input-number v-model="contentForm.product.productId" :min="1" />
          </el-form-item>
        </template>

        <template v-if="selectedBoard && selectedBoard.type === 'BRAND'">
          <el-form-item label="Category ID">
            <el-input-number v-model="contentForm.brand.catId" :min="1" />
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="contentDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="contentSaving" @click="submitContent">Save</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import ImageUpload from "@/components/ImageUpload";
import MyPagination from "@/components/MyPagination";
import StatusSelect from "@/components/StatusSelect";

const manualContentTypes = ["BANNER", "PRODUCT", "BRAND"];

export default {
  name: "HomeBoard",
  components: { ImageUpload, MyPagination, StatusSelect },
  data() {
    return {
      filters: {
        placeType: "HOME",
        placeId: "0",
        status: 10,
      },
      rootCats: [],
      boardTypes: [
        { label: "Banner", value: "BANNER" },
        { label: "Product List", value: "PRODUCT" },
        { label: "Brand", value: "BRAND" },
        { label: "Most Popular", value: "MOST_POPULAR" },
        { label: "Recently Viewed", value: "RECENTLY_VIEWED" },
        { label: "Your Favorite", value: "YOUR_FAVORITE" },
        { label: "Popular Brand", value: "POPULAR_BRAND" },
      ],
      boards: [],
      boardPage: {},
      boardLoading: false,
      selectedBoard: null,

      contents: [],
      contentPage: {},
      contentLoading: false,

      boardDialogVisible: false,
      boardDialogMode: "create",
      boardSaving: false,
      boardForm: this.createBoardForm(),

      contentDialogVisible: false,
      contentDialogMode: "create",
      contentSaving: false,
      contentForm: this.createContentForm(),
    };
  },
  computed: {
    canManageContents() {
      return this.selectedBoard && manualContentTypes.includes(this.selectedBoard.type);
    },
    boardListTitle() {
      if (this.filters.placeType === "MALL_CAT") {
        return "Mall Category Boards";
      }
      return "Home Boards";
    },
    boardTypeOptions() {
      if (this.boardForm.placeType === "MALL_CAT") {
        return this.boardTypes;
      }
      return this.boardTypes.filter(
        (item) => !["MOST_POPULAR", "RECENTLY_VIEWED"].includes(item.value)
      );
    },
  },
  mounted() {
    this.loadRootCats();
    this.listBoards();
  },
  methods: {
    createBoardForm() {
      const filters = this.filters || { placeType: "HOME", placeId: "0" };
      const placeType = filters.placeType || "HOME";
      return {
        id: null,
        placeType,
        placeId: placeType === "HOME" ? "0" : filters.placeId || "",
        title: "",
        type: "BANNER",
        remark: "",
        sortNo: 0,
        status: 10,
      };
    },
    createContentForm() {
      return {
        id: null,
        boardId: this.selectedBoard ? this.selectedBoard.id : null,
        sortNo: 0,
        banner: {
          title: "",
          imageUrl: "",
          actionUrl: "",
        },
        product: {
          productId: null,
        },
        brand: {
          catId: null,
        },
      };
    },
    async loadRootCats() {
      try {
        this.rootCats = await this.axios.get("mall:/api/admin/product-category/rootCats");
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      }
    },
    async changePlaceType() {
      this.filters.placeId = this.filters.placeType === "HOME" ? "0" : "";
      this.selectedBoard = null;
      this.contents = [];
      this.contentPage = {};
      if (this.filters.placeType === "MALL_CAT" && !this.rootCats.length) {
        await this.loadRootCats();
      }
      await this.searchBoards();
    },
    changeBoardFormPlace() {
      this.boardForm.placeId =
        this.boardForm.placeType === "HOME" ? "0" : this.filters.placeId || "";
      if (!this.boardTypeOptions.some((item) => item.value === this.boardForm.type)) {
        this.boardForm.type = "BANNER";
      }
    },
    async searchBoards() {
      this.boardPage.page = 1;
      await this.listBoards();
    },
    async listBoards() {
      this.boardLoading = true;
      const params = Object.assign({}, this.filters, {
        page: this.boardPage.page,
        limit: this.boardPage.limit,
      });
      try {
        const data = await this.axios.form("mall:/api/admin/board/list", params);
        this.boards = data.results || [];
        this.boardPage = data.page || {};
        this.pickBoardAfterList();
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      } finally {
        this.boardLoading = false;
      }
    },
    async pickBoardAfterList() {
      const currentId = this.selectedBoard && this.selectedBoard.id;
      const nextBoard =
        this.boards.find((item) => item.id === currentId) ||
        (this.boards.length ? this.boards[0] : null);

      if (!nextBoard) {
        this.selectedBoard = null;
        this.contents = [];
        this.contentPage = {};
        return;
      }

      this.selectedBoard = nextBoard;
      this.$nextTick(() => {
        if (this.$refs.boardTable) {
          this.$refs.boardTable.setCurrentRow(nextBoard);
        }
      });
      await this.listContents();
    },
    async handleBoardSelect(row) {
      if (!row || (this.selectedBoard && row.id === this.selectedBoard.id)) {
        return;
      }
      this.selectedBoard = row;
      this.contentPage = {};
      await this.listContents();
    },
    async handleBoardPageChange(val) {
      this.boardPage.page = val;
      await this.listBoards();
    },
    async handleBoardLimitChange(val) {
      this.boardPage.limit = val;
      this.boardPage.page = 1;
      await this.listBoards();
    },
    showBoardCreate() {
      this.boardDialogMode = "create";
      this.boardForm = this.createBoardForm();
      this.boardDialogVisible = true;
    },
    async showBoardEdit(row) {
      try {
        this.boardDialogMode = "edit";
        const data = await this.axios.get("mall:/api/admin/board/" + row.id);
        this.boardForm = Object.assign(this.createBoardForm(), data);
        this.boardDialogVisible = true;
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      }
    },
    async submitBoard() {
      if (!this.boardForm.title) {
        this.$message.error("Title is required");
        return;
      }
      if (this.boardForm.placeType === "MALL_CAT" && !this.boardForm.placeId) {
        this.$message.error("Please select a mall category");
        return;
      }
      this.boardSaving = true;
      try {
        const url =
          this.boardDialogMode === "create"
            ? "mall:/api/admin/board/create"
            : "mall:/api/admin/board/update";
        await this.axios.post(url, this.boardForm);
        this.$message.success("Saved");
        this.boardDialogVisible = false;
        await this.listBoards();
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      } finally {
        this.boardSaving = false;
      }
    },
    async deleteBoard(row) {
      try {
        await this.$confirm("Delete this board?", "Confirm", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        await this.axios.form("mall:/api/admin/board/delete", { id: row.id });
        this.$message.success("Deleted");
        if (this.selectedBoard && this.selectedBoard.id === row.id) {
          this.selectedBoard = null;
        }
        await this.listBoards();
      } catch (e) {
        if (e !== "cancel" && e !== "close") {
          this.$notify.error({ title: "Error", message: e.message || e });
        }
      }
    },
    async listContents() {
      if (!this.selectedBoard) {
        this.contents = [];
        this.contentPage = {};
        return;
      }
      this.contentLoading = true;
      const params = {
        boardId: this.selectedBoard.id,
        page: this.contentPage.page,
        limit: this.contentPage.limit,
      };
      try {
        const data = await this.axios.form("mall:/api/admin/board-content/list", params);
        this.contents = data.results || [];
        this.contentPage = data.page || {};
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      } finally {
        this.contentLoading = false;
      }
    },
    async handleContentPageChange(val) {
      this.contentPage.page = val;
      await this.listContents();
    },
    async handleContentLimitChange(val) {
      this.contentPage.limit = val;
      this.contentPage.page = 1;
      await this.listContents();
    },
    showContentCreate() {
      if (!this.canManageContents) {
        return;
      }
      this.contentDialogMode = "create";
      this.contentForm = this.createContentForm();
      this.contentDialogVisible = true;
    },
    showContentEdit(row) {
      if (!this.canManageContents) {
        return;
      }
      this.contentDialogMode = "edit";
      const form = this.createContentForm();
      form.id = row.id;
      form.boardId = this.selectedBoard.id;
      form.sortNo = row.sortNo || 0;

      if (this.selectedBoard.type === "BANNER") {
        form.banner = Object.assign(form.banner, this.getBanner(row));
      } else if (this.selectedBoard.type === "PRODUCT") {
        form.product.productId = this.getProductId(row);
      } else if (this.selectedBoard.type === "BRAND") {
        form.brand.catId = this.getBrandId(row);
      }

      this.contentForm = form;
      this.contentDialogVisible = true;
    },
    async submitContent() {
      if (!this.canManageContents) {
        return;
      }
      const payload = {
        boardId: this.selectedBoard.id,
        sortNo: this.contentForm.sortNo || 0,
      };
      if (this.contentDialogMode === "edit") {
        payload.id = this.contentForm.id;
      }

      if (this.selectedBoard.type === "BANNER") {
        if (!this.contentForm.banner.imageUrl) {
          this.$message.error("Please upload an image first");
          return;
        }
        payload.banner = Object.assign({}, this.contentForm.banner);
      } else if (this.selectedBoard.type === "PRODUCT") {
        payload.product = {
          productId: this.contentForm.product.productId,
        };
      } else if (this.selectedBoard.type === "BRAND") {
        payload.brand = {
          catId: this.contentForm.brand.catId,
        };
      }

      this.contentSaving = true;
      try {
        const url =
          this.contentDialogMode === "create"
            ? "mall:/api/admin/board-content/create"
            : "mall:/api/admin/board-content/update";
        await this.axios.post(url, payload);
        this.$message.success("Saved");
        this.contentDialogVisible = false;
        await this.listContents();
      } catch (e) {
        this.$notify.error({ title: "Error", message: e.message || e });
      } finally {
        this.contentSaving = false;
      }
    },
    async deleteContent(row) {
      try {
        await this.$confirm("Delete this content item?", "Confirm", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        await this.axios.form("mall:/api/admin/board-content/delete", { id: row.id });
        this.$message.success("Deleted");
        await this.listContents();
      } catch (e) {
        if (e !== "cancel" && e !== "close") {
          this.$notify.error({ title: "Error", message: e.message || e });
        }
      }
    },
    getBanner(row) {
      if (row.banner) {
        return row.banner;
      }
      return this.parseContent(row.content) || {};
    },
    getProductId(row) {
      if (row.product && row.product.id) {
        return row.product.id;
      }
      const content = this.parseContent(row.content);
      return content ? content.productId : null;
    },
    getBrandId(row) {
      if (row.brand && row.brand.id) {
        return row.brand.id;
      }
      const content = this.parseContent(row.content);
      return content ? content.catId : null;
    },
    parseContent(content) {
      if (!content) {
        return null;
      }
      if (typeof content === "object") {
        return content;
      }
      try {
        return JSON.parse(content);
      } catch (e) {
        return null;
      }
    },
    imagePreviewList(url) {
      return url ? [url] : [];
    },
    placeLabel(row) {
      if (!row || row.placeType === "HOME") {
        return "Home";
      }
      const cat = this.rootCats.find((item) => String(item.id) === String(row.placeId));
      return cat ? `Mall: ${cat.name}` : `Mall: ${row.placeId || "-"}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.home-board-page {
  .panel-card {
    margin-bottom: 16px;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .content-alert {
    margin-bottom: 12px;
  }

  .banner-cell {
    display: flex;
    align-items: center;
  }

  .banner-thumb {
    width: 72px;
    height: 72px;
    margin-right: 10px;
    flex: 0 0 auto;
  }

  .content-text {
    min-width: 0;
  }

  .content-title {
    font-weight: 600;
    color: #303133;
    word-break: break-word;
  }

  .content-link {
    margin-top: 4px;
    color: #909399;
    font-size: 12px;
    word-break: break-all;
  }
}
</style>
